import { StateService } from "@uirouter/angular";
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Output } from "@angular/core";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { IGoal, OkrMethodologySettings } from "@gtmhub/goals/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { ItemType } from "@gtmhub/okrs/alignment-tree/models";
import { formatAttainmentProgressValue } from "@gtmhub/okrs/metrics/utils";
import { isCurrentUserOwner } from "@gtmhub/sessions/utils/granular-permission.util";
import { CurrentUserService } from "@gtmhub/users";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { isUnknownAssignee } from "@webapp/assignees/utils/assignee.utils";
import { localize } from "@webapp/localization/utils/localization.utils";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { IMetricContributorActionHandlers, IMetricContributorContext } from "@webapp/okrs/metrics/models/metric-contributor-dropdown.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { getDisplayableOwnerIds, getProgressDescription } from "@webapp/okrs/utils/utils";
import { Session } from "@webapp/sessions/models/sessions.model";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";

@Component({
  selector: "okr-contributor-item",
  templateUrl: "./okr-contributor-item.component.html",
  styleUrls: ["./okr-contributor-item.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OkrContributorItemComponent implements OnInit {
  @Input() public item: IGoal | Metric;
  @Input() public goal: IGoal;
  @Input() public methodologySettings: OkrMethodologySettings;
  @Input() public planningSession: Session;
  @Input() public coloringStrategy: string;
  @Input() public allowedActions: string[];
  @Input() public goalChildren: object[];
  @Input() public isTrialAccount: boolean;
  @Input() public isAllowedToSubmitToMarketplace: boolean;
  @Input() public assigneesIdMap: Record<string, Assignee>;

  @Output() public readonly addChildObjective: EventEmitter<{ itemId: string; itemType: ItemType; itemSessionId: string }> = new EventEmitter();
  @Output() public readonly openMoveMetricModal: EventEmitter<{ metric: Metric }> = new EventEmitter();
  @Output() public readonly openCloneMetricModal: EventEmitter<{ metric: Metric }> = new EventEmitter();
  @Output() public readonly openDisableSyncModal: EventEmitter<{ metric: Metric }> = new EventEmitter();
  @Output() public readonly confirmDeleteMetric: EventEmitter<{ itemId: string }> = new EventEmitter();
  @Output() public readonly handleOpenCascadingForm: EventEmitter<{ metric: Metric; goalName: string }> = new EventEmitter();
  @Output() public readonly copyItemUrlToClipboard: EventEmitter<{ itemId: string; type: ItemType }> = new EventEmitter();
  @Output() public readonly openUpdateMetricModal: EventEmitter<{ metricId: string }> = new EventEmitter();

  public type: ItemType;
  public metric: Metric;
  public isContributingMetric: boolean;
  public contributorMetric: Metric;
  public contributorGoal: IGoal;
  public metricContributorActionHandlers: IMetricContributorActionHandlers;
  public metricContributorContext: IMetricContributorContext;
  public dropdownGoalContributorItems: DropdownMenuItem[];
  public ownerIds: string[];

  constructor(
    private state: StateService,
    private currentUserService: CurrentUserService,
    private progressColoring: ProgressColoring,
    @Inject("$rootScope") private rootScope: IGtmhubRootScopeService
  ) {}

  public ngOnInit(): void {
    this.type = this.item.childType;
    if (this.type === "metric") {
      this.contributorMetric = this.item as Metric;
    } else {
      this.contributorGoal = this.item as IGoal;
    }
    this.ownerIds = getDisplayableOwnerIds(this.item, this.type, this.methodologySettings);
    this.setMetricContributorActionHandlersAndContext();
    this.dropdownGoalContributorItems = this.buildDropdownMenu();
  }

  private buildDropdownMenu(): DropdownMenuItem[] {
    if (!this.contributorGoal) return;

    const menuItemBuilder = new DropdownMenuItemBuilder();
    const dropdownOptions: DropdownMenuItem[] = [];

    const openGoal = menuItemBuilder
      .setKey("open_item")
      .setAction({ state: "^.goal", params: { id: this.contributorGoal.id } })
      .setClasses(["action bordered-top add-key-result-button-from-drop-down'"])
      .build();

    dropdownOptions.push(openGoal);

    const createMetric = menuItemBuilder
      .setKey("add_metric")
      .setAction({
        state: "._createMetric.createMetric",
        params: { id: this.contributorGoal.id, planningSessionId: this.contributorGoal.sessionId, flowName: "create_metric_form_objective_dropdown" },
      })
      .setClasses(["action"])
      .build();

    dropdownOptions.push(createMetric);

    if (this.contributorGoal.attainmentTypeString === "average_children") {
      const addChildGoal = menuItemBuilder
        .setKey("add_child_goal")
        .setAction({
          state: "._createMetric.createMetric",
          params: { id: this.contributorGoal.id, planningSessionId: this.contributorGoal.sessionId, flowName: "create_metric_form_objective_dropdown" },
        })
        .setClasses(["action"])
        .build();
      dropdownOptions.push(addChildGoal);
    }

    const copyUrl = menuItemBuilder
      .setKey("copy_url")
      .setAction({ handler: (): void => this.copyItemUrlToClipboard.emit({ itemId: this.contributorGoal.id, type: "goal" }) })
      .setClasses(["action"])
      .setSkipFromTabOrder()
      .build();
    dropdownOptions.push(copyUrl);

    if (!this.isTrialAccount && this.isAllowedToSubmitToMarketplace) {
      const submitToMarketplace = menuItemBuilder
        .setKey("submit_to_the_marketplace")
        .setAction({ state: "._marketplace.submit", params: { goalToBeSubmittedId: this.contributorGoal.id } })
        .setClasses(["action"])
        .setUiType({ uiType: "button" })
        .setSkipFromTabOrder()
        .build();
      dropdownOptions.push(submitToMarketplace);
    }
    return dropdownOptions;
  }

  public openGoal(goalId: string): void {
    this.state.go("^.goal", { id: goalId });
  }

  public onCopyItemToUrlClipboard = (): void => {
    if (this.contributorGoal) {
      this.copyItemUrlToClipboard.emit({ itemId: this.contributorGoal.id, type: "goal" });
    } else {
      this.copyItemUrlToClipboard.emit({ itemId: this.contributorMetric.id, type: "metric" });
    }
  };

  public onUpdateMetric = (): void => {
    this.openUpdateMetricModal.emit({ metricId: this.contributorMetric.id });
  };
  public onHandleOpenCascadingForm = (): void => {
    this.handleOpenCascadingForm.emit({ metric: this.contributorMetric, goalName: this.goal.name });
  };
  public onAddObjectiveClick(): void {
    const data = {
      itemId: this.goal.id,
      itemType: this.type,
      itemSessionId: this.goal.sessionId,
    };
    this.addChildObjective.emit(data);
  }
  public onOpenMoveMetricModal = (): void => {
    this.openMoveMetricModal.emit({ metric: this.contributorMetric });
  };
  public onOpenCloneMetricModal = (): void => {
    this.openCloneMetricModal.emit({ metric: this.contributorMetric });
  };
  public onOpenDisableSyncModal = (): void => {
    this.openDisableSyncModal.emit({ metric: this.contributorMetric });
  };
  public onOpenDeleteMetric = (): void => {
    this.confirmDeleteMetric.emit({ itemId: this.contributorMetric.id });
  };

  public onKeyResultNamePress(event?: KeyboardEvent): void {
    if (event?.key === "Enter") {
      event.preventDefault();
      this.state.go("._metric.metric", { metricId: this.contributorMetric.id });
    } else if (!event) {
      this.state.go("._metric.metric", { metricId: this.contributorMetric.id });
    }
  }

  public metricAriaLabelDescription(): string {
    if (this.type !== "metric") {
      return "";
    }
    const metricTargetDescription = document.getElementById(`metric-target-container-${this.contributorMetric.id}`)?.textContent;
    let confidenceDescription = "";
    if (this.contributorMetric.confidence) {
      const confidenceLabel = document.getElementById(`confidence-preview-${this.contributorMetric.id}`)?.textContent;
      confidenceDescription = `${localize("confidence_level")}: ${confidenceLabel} `;
    }
    const colorSetup = this.progressColoring.getColoring();
    const progressDescription = getProgressDescription(colorSetup, this.contributorMetric.attainment, this.contributorMetric.color);
    return `${this.contributorMetric.name} ${metricTargetDescription} ${confidenceDescription} ${this.metricAssigneeDescription} ${progressDescription}`;
  }

  public metricAssigneeDescription(): string {
    let assigneesDescription = "";
    if (this.contributorMetric?.ownerIds === undefined) {
      return assigneesDescription;
    }
    for (const assigneeId of this.contributorMetric.ownerIds) {
      const assignee = this.assigneesIdMap[assigneeId];
      const isDeactivated = assignee && !isUnknownAssignee(assignee) && assignee.isActive === false;
      const isUnknown = !assignee || isUnknownAssignee(assignee);
      let assigneeDescription = "";
      if (!isUnknown && !isDeactivated) {
        assigneeDescription = assignee.name + " ";
      } else if (isDeactivated && !isUnknown) {
        assigneeDescription = `${assignee.name} ${localize("deactivated")}`;
      } else {
        assigneeDescription = localize("deleted_user");
      }
      assigneesDescription = assigneesDescription + assigneeDescription + " ";
    }
    return assigneesDescription;
  }

  private setMetricContributorActionHandlersAndContext(): void {
    this.metricContributorActionHandlers = {
      move: this.onOpenMoveMetricModal,
      cascade: this.onHandleOpenCascadingForm,
      clone: this.onOpenCloneMetricModal,
      disableSync: this.onOpenDisableSyncModal,
      copyUrl: this.onCopyItemToUrlClipboard,
      delete: this.onOpenDeleteMetric,
      update: this.onUpdateMetric,
    };
    this.metricContributorContext = {
      metric: this.contributorMetric as Metric,
      goal: this.goal as Goal,
      session: this.planningSession,
      allowedActions: this.allowedActions,
    };
  }
  public formatValue(attainment: number): number {
    return formatAttainmentProgressValue(attainment);
  }
  public canUpdateMetric(): boolean {
    if (!this.contributorMetric) {
      return false;
    }
    const isMetricOverdue = this.contributorMetric.daysLeft < 0;
    return (
      !isMetricOverdue &&
      this.rootScope.hasPermission("ManageGoals") &&
      !this.contributorMetric.sourceMetricId &&
      this.goal.closedStatus &&
      this.goal.closedStatus.status !== "abandoned" &&
      (this.currentUserService.allowedActionsSync(this.planningSession.access).includes("update") || this.isUserCoOwner())
    );
  }
  private isUserCoOwner(): boolean {
    if (!this.contributorMetric.ownerIds) {
      return false;
    }
    return isCurrentUserOwner(
      this.contributorMetric.ownerIds,
      this.currentUserService.getCurrentUserShadowRoles().map((role) => role.id)
    );
  }
}
